import "./QITestDrive.scss";
import { useState, useEffect, useRef } from "react";
import FormFieldWithLabel from "../FormFieldWithLabel/FormFieldWithLabel.jsx";
import { produce } from "immer";
import {
  CheckboxWithCollapsibleText,
  CollapsibleText,
} from "../CheckBoxComponents/CheckBoxWithCollapsibleText.jsx";
import { SignatureCanvas } from "react-signature-canvas";
import { ReactComponent as Bin } from "../../assets/icons/bin.svg";
import { ReactComponent as Add } from "../../assets/icons/add-features.svg";
import { useDealershipContext } from "../../contexts/dealershipContext.jsx";
import { LuSend } from "react-icons/lu";
import { useDealershipUserContext } from "../../contexts/dealershipUserContext.jsx";
import { sendFinanceAndInsuranceFormText } from "../../services/dealership-user.js";
import { DEALERSHIP_CONFIG } from "../../config/dealershipConfig.js";
import { useUserTrackingContext } from "../../contexts/userTrackingContext.jsx";

function TestDriveFormFields({
  quizQuestions,
  setQuizQuestions,
  currentQuestionViewIndex,
  childIndex,
  user,
}) {
  const testDriveParentQuestion = quizQuestions[currentQuestionViewIndex];
  const testDriveChildQuestion = testDriveParentQuestion.children[childIndex];
  const [disabled, setDisabled] = useState(
    testDriveParentQuestion?.disabled || false
  );

  const approvalDetails =
    testDriveChildQuestion?.testDriveApprovalDetails || {};
  const customerInfo = approvalDetails?.customerInfo || {};
  const vehicleInfo = approvalDetails?.vehicleInfo || {};
  const testDrivePeriod = approvalDetails?.testDrivePeriod || {};

  const [customerName, setCustomerName] = useState(customerInfo.name || "");
  const [customerAddress, setCustomerAddress] = useState(
    customerInfo.address || ""
  );
  const [customerPhone, setCustomerPhone] = useState(customerInfo.phone || "");
  const [vehicleYear, setVehicleYear] = useState(vehicleInfo.year || "");
  const [vehicleMake, setVehicleMake] = useState(vehicleInfo.make || "");
  const [vehicleModel, setVehicleModel] = useState(vehicleInfo.model || "");
  const [vehicleStock, setVehicleStock] = useState(vehicleInfo.stock || "");
  const [demoLicencePlate, setDemoLicencePlate] = useState(
    vehicleInfo.demonstrationLicencePlate || ""
  );
  const [dateOut, setDateOut] = useState(testDrivePeriod.dateOut || "");
  const [timeOut, setTimeOut] = useState(testDrivePeriod.timeOut || "");
  const [dateReturnBy, setDateReturnBy] = useState(
    testDrivePeriod.dateReturnBy || ""
  );
  const [timeReturnBy, setTimeReturnBy] = useState(
    testDrivePeriod.timeReturnBy || ""
  );

  useEffect(() => {
    setDisabled(quizQuestions[currentQuestionViewIndex]?.disabled);
  }, [quizQuestions]);

  useEffect(() => {
    if (!customerInfo.name && user?.firstName) {
      const prefillName = `${user.firstName} ${user.lastName || ""}`.trim();
      const prefillPhone = user.phoneNo || "";
      setCustomerName(prefillName);
      setCustomerPhone(prefillPhone);
      setQuizQuestions(
        produce((draft) => {
          const child = draft[currentQuestionViewIndex].children[childIndex];
          if (!child.testDriveApprovalDetails)
            child.testDriveApprovalDetails = {};
          if (!child.testDriveApprovalDetails.customerInfo)
            child.testDriveApprovalDetails.customerInfo = {};
          child.testDriveApprovalDetails.customerInfo.name = prefillName;
          child.testDriveApprovalDetails.customerInfo.phone = prefillPhone;
        })
      );
    }
  }, []);

  useEffect(() => {
    const ad = testDriveChildQuestion?.testDriveApprovalDetails || {};
    const ci = ad?.customerInfo || {};
    const vi = ad?.vehicleInfo || {};
    const tp = ad?.testDrivePeriod || {};
    setCustomerName(ci.name || "");
    setCustomerAddress(ci.address || "");
    setCustomerPhone(ci.phone || "");
    setVehicleYear(vi.year || "");
    setVehicleMake(vi.make || "");
    setVehicleModel(vi.model || "");
    setVehicleStock(vi.stock || "");
    setDemoLicencePlate(vi.demonstrationLicencePlate || "");
    setDateOut(tp.dateOut || "");
    setTimeOut(tp.timeOut || "");
    setDateReturnBy(tp.dateReturnBy || "");
    setTimeReturnBy(tp.timeReturnBy || "");
  }, [testDriveChildQuestion]);

  const updateCustomerInfo = (field, value) => {
    setQuizQuestions(
      produce((draft) => {
        const child = draft[currentQuestionViewIndex].children[childIndex];
        if (!child.testDriveApprovalDetails)
          child.testDriveApprovalDetails = {};
        if (!child.testDriveApprovalDetails.customerInfo)
          child.testDriveApprovalDetails.customerInfo = {};
        child.testDriveApprovalDetails.customerInfo[field] = value;
      })
    );
  };

  const updateVehicleInfo = (field, value) => {
    setQuizQuestions(
      produce((draft) => {
        const child = draft[currentQuestionViewIndex].children[childIndex];
        if (!child.testDriveApprovalDetails)
          child.testDriveApprovalDetails = {};
        if (!child.testDriveApprovalDetails.vehicleInfo)
          child.testDriveApprovalDetails.vehicleInfo = {};
        child.testDriveApprovalDetails.vehicleInfo[field] = value;
      })
    );
  };

  const updateTestDrivePeriod = (field, value) => {
    setQuizQuestions(
      produce((draft) => {
        const child = draft[currentQuestionViewIndex].children[childIndex];
        if (!child.testDriveApprovalDetails)
          child.testDriveApprovalDetails = {};
        if (!child.testDriveApprovalDetails.testDrivePeriod)
          child.testDriveApprovalDetails.testDrivePeriod = {};
        child.testDriveApprovalDetails.testDrivePeriod[field] = value;
      })
    );
  };

  return (
    <div className="test-drive-form-section">
      <p className="test-drive-form-section__header">Customer Information</p>
      <div className="combine-fields">
        <FormFieldWithLabel
          label="Name"
          type="text"
          id="customer-name"
          placeholder=""
          value={customerName}
          disabled={disabled}
          onChange={(e) => {
            setCustomerName(e.target.value);
            updateCustomerInfo("name", e.target.value);
          }}
          errorMessage=""
        />
        <FormFieldWithLabel
          label="Address"
          type="text"
          id="customer-address"
          placeholder=""
          value={customerAddress}
          disabled={disabled}
          onChange={(e) => {
            setCustomerAddress(e.target.value);
            updateCustomerInfo("address", e.target.value);
          }}
          errorMessage=""
        />
        <FormFieldWithLabel
          label="Phone"
          type="text"
          id="customer-phone"
          placeholder=""
          value={customerPhone}
          disabled={disabled}
          onChange={(e) => {
            setCustomerPhone(e.target.value);
            updateCustomerInfo("phone", e.target.value);
          }}
          errorMessage=""
        />
      </div>

      <p className="test-drive-form-section__header">Vehicle Information</p>
      <div className="combine-fields">
        <FormFieldWithLabel
          label="Year"
          type="text"
          id="vehicle-year"
          placeholder=""
          value={vehicleYear}
          disabled={disabled}
          onChange={(e) => {
            setVehicleYear(e.target.value);
            updateVehicleInfo("year", e.target.value);
          }}
          errorMessage=""
        />
        <FormFieldWithLabel
          label="Make"
          type="text"
          id="vehicle-make"
          placeholder=""
          value={vehicleMake}
          disabled={disabled}
          onChange={(e) => {
            setVehicleMake(e.target.value);
            updateVehicleInfo("make", e.target.value);
          }}
          errorMessage=""
        />
        <FormFieldWithLabel
          label="Model"
          type="text"
          id="vehicle-model"
          placeholder=""
          value={vehicleModel}
          disabled={disabled}
          onChange={(e) => {
            setVehicleModel(e.target.value);
            updateVehicleInfo("model", e.target.value);
          }}
          errorMessage=""
        />
      </div>
      <div className="combine-fields">
        <FormFieldWithLabel
          label="Stock"
          type="text"
          id="vehicle-stock"
          placeholder=""
          value={vehicleStock}
          disabled={disabled}
          onChange={(e) => {
            setVehicleStock(e.target.value);
            updateVehicleInfo("stock", e.target.value);
          }}
          errorMessage=""
        />
        <FormFieldWithLabel
          label="Demonstration Licence Plate"
          type="text"
          id="demo-licence-plate"
          placeholder=""
          value={demoLicencePlate}
          disabled={disabled}
          onChange={(e) => {
            setDemoLicencePlate(e.target.value);
            updateVehicleInfo("demonstrationLicencePlate", e.target.value);
          }}
          errorMessage=""
        />
      </div>

      <p className="test-drive-form-section__header">Test Drive Period</p>
      <div className="combine-fields">
        <FormFieldWithLabel
          label="Date Out"
          type="date"
          id="date-out"
          placeholder=""
          value={dateOut}
          disabled={disabled}
          onClick={(e) => {
            if (e.target.showPicker) e.target.showPicker();
          }}
          onChange={(e) => {
            setDateOut(e.target.value);
            updateTestDrivePeriod("dateOut", e.target.value);
          }}
          errorMessage=""
        />
        <FormFieldWithLabel
          label="Time Out"
          type="time"
          id="time-out"
          placeholder=""
          value={timeOut}
          disabled={disabled}
          onClick={(e) => {
            if (e.target.showPicker) e.target.showPicker();
          }}
          onChange={(e) => {
            setTimeOut(e.target.value);
            updateTestDrivePeriod("timeOut", e.target.value);
          }}
          errorMessage=""
        />
      </div>
      <div className="combine-fields">
        <FormFieldWithLabel
          label="Date Vehicle Must be Returned by"
          type="date"
          id="date-return-by"
          placeholder=""
          value={dateReturnBy}
          disabled={disabled}
          onClick={(e) => {
            if (e.target.showPicker) e.target.showPicker();
          }}
          onChange={(e) => {
            setDateReturnBy(e.target.value);
            updateTestDrivePeriod("dateReturnBy", e.target.value);
          }}
          errorMessage=""
        />
        <FormFieldWithLabel
          label="Time Vehicle Must be Returned by"
          type="time"
          id="time-return-by"
          placeholder=""
          value={timeReturnBy}
          disabled={disabled}
          onClick={(e) => {
            if (e.target.showPicker) e.target.showPicker();
          }}
          onChange={(e) => {
            setTimeReturnBy(e.target.value);
            updateTestDrivePeriod("timeReturnBy", e.target.value);
          }}
          errorMessage=""
        />
      </div>
    </div>
  );
}

function ApplicantDetailsForm({
  applicantInstance,
  setTotalApplicants,
  totalApplicants,
  quizQuestions,
  setQuizQuestions,
  currentQuestionViewIndex,
  childIndex,
  financePreApproval,
}) {
  const getMaxDOB = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = (new Date().getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return `${year - 15}-${month}-${day}`;
  };

  const testDriveParentQuestion = quizQuestions[currentQuestionViewIndex];
  const testDriveChildQuestion = testDriveParentQuestion.children[childIndex];

  const currentApplicantDetails =
    testDriveChildQuestion?.applicantDetails?.[applicantInstance] || {};

  const [dateOfBirth, setDateOfBirth] = useState(
    currentApplicantDetails.dateOfBirth || ""
  );
  const [dateOfBirthError, setDateOfBirthError] = useState("");
  const [employmentStatus, setEmploymentStatus] = useState(
    currentApplicantDetails.employmentStatus || ""
  );
  const [employmentStatusError, setEmploymentStatusError] = useState("");
  const [sinNumber, setSinNumber] = useState(
    currentApplicantDetails.sinNumber || ""
  );
  const [sinNumberError, setSinNumberError] = useState("");
  const [experience, setExperience] = useState(
    currentApplicantDetails.experience || ""
  );
  const [experienceError, setExperienceError] = useState("");
  const [firstName, setFirstName] = useState(
    currentApplicantDetails.firstName || ""
  );
  const [lastName, setLastName] = useState(
    currentApplicantDetails.lastName || ""
  );
  const [disabled, setDisabled] = useState(
    testDriveParentQuestion?.disabled || false
  );
  const [address, setAddress] = useState(currentApplicantDetails.address || "");

  const { dealershipName } = useDealershipContext();

  const signatureTerms = `*By clicking ‘Submit’ and signing electronically, you consent to the use of your electronic signature as legally binding under applicable electronic signature laws. You also authorize ${dealershipName} to process your information for test drive scheduling and/or pre-approval purposes.`;

  const employmentStatusOptions = [
    "",
    "Full-Time",
    "Part-Time",
    "Self-employed/Contractor",
    "Unemployed",
    "Student",
    "Retired",
    "Other",
  ];

  const experienceOptions = [
    "",
    "0-1 Year",
    "1-2 Years",
    "⁠3-4 Years",
    "5+ Years",
  ];

  useEffect(() => {
    setDisabled(quizQuestions[currentQuestionViewIndex]?.disabled);
  }, [quizQuestions]);

  useEffect(() => {
    setDateOfBirth(currentApplicantDetails.dateOfBirth || "");
    setEmploymentStatus(currentApplicantDetails.employmentStatus || "");
    setExperience(currentApplicantDetails.experience || "");
    setSinNumber(currentApplicantDetails.sinNumber || "");
    setAddress(currentApplicantDetails.address || "");
  }, [currentApplicantDetails]);

  function addApplicantInstance() {
    if (totalApplicants < 2) {
      setTotalApplicants(totalApplicants + 1);
      setQuizQuestions(
        produce((quizQuestionsDraft) => {
          quizQuestionsDraft[currentQuestionViewIndex].children[
            childIndex
          ].applicantDetails.push({});
        })
      );
    }
  }

  function removeApplicantInstance() {
    setQuizQuestions(
      produce((quizQuestionsDraft) => {
        quizQuestionsDraft[currentQuestionViewIndex].children[
          childIndex
        ].applicantDetails.splice(applicantInstance, 1);
      })
    );
    setTotalApplicants(totalApplicants - 1);
  }

  const validateEmploymentStatus = (employmentStatus) => {
    if (
      !employmentStatus &&
      employmentStatusOptions.includes(employmentStatus)
    ) {
      setEmploymentStatusError("Wrong Value for Employment Status");
      return false;
    } else {
      return true;
    }
  };

  const validateDateOfBirth = (dateOfBirth) => {
    const dateOfBirthRegex =
      /^(19|20)\d{2}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
    if (dateOfBirth && !dateOfBirthRegex.test(dateOfBirth)) {
      setDateOfBirthError("Invalid Date of Birth");
      return false;
    }
    setDateOfBirthError("");
    return true;
  };

  const validateSinNumber = (sinNumber) => {
    const sinNumberRegex = "";
    if (sinNumber && !sinNumberRegex.test(employmentStatus)) {
      setSinNumberError("Invalid SIN number");
      return false;
    }
    setSinNumberError("");
    return true;
  };

  const validateExperience = (employment) => {
    const experienceRegex = /^\S.*$/;
    if (experience && !experienceRegex.test(employment)) {
      setExperienceError("Invalid Employment Field");
      return false;
    }
    setExperienceError("");
    return true;
  };

  return (
    <>
      {financePreApproval && (
        <div className="multiple-applicant-box">
          {totalApplicants < 2 && (
            <div className="add-co-applicant-container">
              <Add
                className="add-co-applicant-btn"
                onClick={() => {
                  addApplicantInstance();
                }}
              />
              <p className="add-co-applicant-text">Add More Applicants</p>
            </div>
          )}
          {totalApplicants > 1 && (
            <div className="applicant-title-box">
              <p className="applicant-title">
                Applicant {applicantInstance + 1}
              </p>
              {applicantInstance > 0 && (
                <p
                  onClick={() => removeApplicantInstance()}
                  className="applicant-remove-text"
                >
                  (Remove -)
                </p>
              )}
            </div>
          )}
          <div className="applicant-details-form">
            {applicantInstance > 0 && (
              <div className="combine-fields">
                <FormFieldWithLabel
                  label="First Name"
                  type="text"
                  id="first-name"
                  placeholder=""
                  value={firstName}
                  disabled={disabled}
                  onChange={(e) => {
                    setQuizQuestions(
                      produce((quizQuestionsDraft) => {
                        quizQuestionsDraft[currentQuestionViewIndex].children[
                          childIndex
                        ].applicantDetails[applicantInstance].firstName =
                          e.target.value;
                      })
                    );
                    setFirstName(e.target.value);
                  }}
                  errorMessage={""}
                />
                <FormFieldWithLabel
                  label="Last Name"
                  type="text"
                  id="last-name"
                  placeholder=""
                  value={lastName}
                  disabled={disabled}
                  onChange={(e) => {
                    setQuizQuestions(
                      produce((quizQuestionsDraft) => {
                        quizQuestionsDraft[currentQuestionViewIndex].children[
                          childIndex
                        ].applicantDetails[applicantInstance].lastName =
                          e.target.value;
                      })
                    );
                    setLastName(e.target.value);
                  }}
                  errorMessage={""}
                />
              </div>
            )}
            <div className="combine-fields">
              <FormFieldWithLabel
                label="Date of birth"
                type="date"
                id="dateOfBirth"
                placeholder="2002-08-20"
                value={dateOfBirth}
                max={getMaxDOB()}
                disabled={disabled}
                onClick={(e) => {
                  if (e.target.showPicker) {
                    e.target.showPicker();
                  }
                }}
                onChange={(e) => {
                  if (validateDateOfBirth(e.target.value)) {
                    setQuizQuestions(
                      produce((quizQuestionsDraft) => {
                        quizQuestionsDraft[currentQuestionViewIndex].children[
                          childIndex
                        ].applicantDetails[applicantInstance].dateOfBirth =
                          e.target.value;
                      })
                    );
                  }
                  setDateOfBirth(e.target.value);
                }}
                errorMessage={dateOfBirthError}
              />
              <div className="dropdown-group">
                <label htmlFor="employment-status">Employment Status</label>
                <select
                  value={employmentStatus}
                  onChange={(e) => {
                    if (validateEmploymentStatus(e.target.value)) {
                      setQuizQuestions(
                        produce((quizQuestionsDraft) => {
                          quizQuestionsDraft[currentQuestionViewIndex].children[
                            childIndex
                          ].applicantDetails[
                            applicantInstance
                          ].employmentStatus = e.target.value;
                        })
                      );
                    }
                    setEmploymentStatus(e.target.value);
                  }}
                  errorMessage={employmentStatusError}
                  disabled={disabled}
                  className="employment-dropdown"
                >
                  {employmentStatusOptions.map((opt, index) => {
                    return (
                      <option key={index} value={opt}>
                        {opt}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
            <div className="combine-fields">
              {/* <FormFieldWithLabel
                label="SIN (coming soon)"
                type="password"
                id="sinNumber"
                placeholder=""
                value={sinNumber}
                disabled={true}
                onChange={(e) => {
                  if (validateSinNumber(e.target.value)) {
                    setQuizQuestions(
                      produce((quizQuestionsDraft) => {
                        quizQuestionsDraft[currentQuestionViewIndex].children[
                          childIndex
                        ].applicantDetails[applicantInstance].sinNumber =
                          e.target.value;
                      })
                    );
                  }
                  setSinNumber(e.target.value);
                }}
                errorMessage={sinNumberError}
              /> */}

              <FormFieldWithLabel
                label="Address"
                type="text"
                id="address"
                placeholder=""
                value={address}
                onChange={(e) => {
                  setQuizQuestions(
                    produce((quizQuestionsDraft) => {
                      quizQuestionsDraft[currentQuestionViewIndex].children[
                        childIndex
                      ].applicantDetails[applicantInstance].address =
                        e.target.value;
                    })
                  );
                  setAddress(e.target.value);
                }}
              />
              <div className="dropdown-group">
                <label htmlFor="experience">
                  How long have you worked here?
                </label>
                <select
                  value={experience}
                  onChange={(e) => {
                    if (validateExperience(e.target.value)) {
                      setQuizQuestions(
                        produce((quizQuestionsDraft) => {
                          quizQuestionsDraft[currentQuestionViewIndex].children[
                            childIndex
                          ].applicantDetails[applicantInstance].experience =
                            e.target.value;
                        })
                      );
                    }
                    setExperience(e.target.value);
                  }}
                  disabled={disabled}
                  errorMessage={experienceError}
                  className="employment-dropdown"
                >
                  {experienceOptions.map((opt, index) => {
                    return (
                      <option key={index} value={opt}>
                        {opt}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="signature-area-container">
        <div>Signature</div>
        <div className="signature-box-container">
          <SignatureBox
            applicantInstance={applicantInstance}
            setQuizQuestions={setQuizQuestions}
            quizQuestions={quizQuestions}
            currentQuestionViewIndex={currentQuestionViewIndex}
            childIndex={childIndex}
          />
        </div>
        <div className="signature-terms-text" style={{ marginTop: "-10px" }}>
          {signatureTerms}
          {/* <CollapsibleText textHeader = "By Signing..." text = {signatureTerms}/> */}
        </div>
      </div>
    </>
  );
}

function SignatureBox({
  applicantInstance,
  setQuizQuestions,
  quizQuestions,
  currentQuestionViewIndex,
  childIndex,
}) {
  const [signaturePenColor, setSignaturePenColor] = useState("black");
  const testDriveParentQuestion = quizQuestions[currentQuestionViewIndex];
  const testDriveChildQuestion = testDriveParentQuestion.children[childIndex];
  const currentApplicantDetails =
    testDriveChildQuestion?.applicantDetails?.[applicantInstance] || {};
  const [disabled, setDisabled] = useState(
    testDriveParentQuestion?.disabled || false
  );

  const sigRef = useRef();

  // useEffect(() => {

  // }, [signatureData]);

  useEffect(() => {
    const disabledTemp = quizQuestions[currentQuestionViewIndex]?.disabled;
    setDisabled(disabledTemp);
  }, [quizQuestions]);

  useEffect(() => {
    const signatureData = currentApplicantDetails.signature || "";
    //Logic if need to clear Signature while Editing
    //const signatureData = !disabled ? '' : (currentApplicantDetails.signature || "");
    //if(!disabled) sigRef.current.clear();
    if (sigRef.current && signatureData) {
      sigRef.current.clear();
      sigRef.current.fromDataURL(signatureData);
    }
  }, [disabled]);

  return (
    <div className="multiple-applicant-box">
      <div className="signature-box">
        <SignatureCanvas
          penColor={signaturePenColor}
          ref={sigRef}
          backgroundColor="white"
          velocityFilterWeight={1}
          dotSIZE={10}
          onEnd={() => {
            setQuizQuestions(
              produce((quizQuestionsDraft) => {
                quizQuestionsDraft[currentQuestionViewIndex].children[
                  childIndex
                ].applicantDetails[applicantInstance].signature =
                  sigRef.current.toDataURL();
              })
            );
          }}
          canvasProps={{ width: 650, height: 250, className: "sigCanvas" }}
        />
        {disabled && (
          <div
            className="signature-overlay"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255,0)",
              zIndex: 10,
              cursor: "not-allowed",
            }}
          />
        )}
        <div className="signature-box__tools">
          {/* <SignaturePen onClick={() => {setSignaturePenColor('black')}} style={{color : 'red'}} className='sgn-pen-black'/>
            <SignaturePen onClick={() => {setSignaturePenColor('red')}} fill = "red" className='sgn-pen-red'/>
            <SignaturePen onClick={() => {setSignaturePenColor('blue')}} fill = "blue" className='sgn-pen-blue'/>          */}
          <Bin
            className="sgn-clear"
            onClick={() => {
              if (!disabled) {
                sigRef.current.clear();
                setQuizQuestions(
                  produce((quizQuestionsDraft) => {
                    quizQuestionsDraft[currentQuestionViewIndex].children[
                      childIndex
                    ].applicantDetails[applicantInstance].signature = "";
                  })
                );
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default function QITestDrive({
  currentQuestionViewIndex,
  quizQuestions,
  setQuizQuestions,
  childIndex,
}) {
  const testDriveParentQuestion = quizQuestions[currentQuestionViewIndex];
  const testDriveChildQuestion = testDriveParentQuestion.children[childIndex];
  const [additionalNotes, setAdditionalNotes] = useState(
    testDriveChildQuestion.additionalNotes || ""
  );
  const [testDriveApproval, setTestDriveApproval] = useState(
    testDriveChildQuestion.testDriveApproval || false
  );
  const [financePreApproval, setFinancePreApproval] = useState(
    testDriveChildQuestion.financePreApproval || false
  );
  const [totalApplicants, setTotalApplicants] = useState(
    testDriveChildQuestion?.applicantDetails?.length || 1
  );
  const [disabled, setDisabled] = useState(
    quizQuestions[currentQuestionViewIndex]?.disabled || false
  );
  const currentApplicantDetails =
    testDriveChildQuestion?.applicantDetails?.[0] || {};
  const firstApplicantSignatureData = currentApplicantDetails.signature || "";
  const { dealershipName, dealershipId } = useDealershipContext();
  const { user } = useDealershipUserContext();
  const { trackEvent } = useUserTrackingContext();
  const [showCaslDisclaimer, setShowCaslDisclaimer] = useState(true);
  const [smsOptInStatus, setSmsOptInStatus] = useState(null);
  const [financingFormStatus, setFinancingFormStatus] = useState("");
  let testDriveApprovalDisclaimer;

  const financePreApprovalDisclaimer = `Portions of the above information will be submitted for the purposes of obtaining credit. The undersigned authorizes any of the Applewood Group Dealerships to make such inquiries as are necessary to obtain credit information and authorize banks, financial institutions, insurers, and credit references to release information regarding their accounts.
  
  You acknowledge that, for the initial application, we may obtain two credit bureaus on each Applicant and Co-Applicant.`;
  if (dealershipId.startsWith("morrey")) {
    testDriveApprovalDisclaimer =
      "This approval is only for a test drive and does not impact your credit or indicate any financial pre-approval.";
  } else {
    testDriveApprovalDisclaimer = `This Test Drive Agreement (the “Agreement”) is made between the above noted customer (the “Customer”) and ${dealershipName} (the “Dealer”) to set out the terms and conditions under which the Dealer permits the Customer to operate the vehicle identified above (the “Vehicle”). The Customer acknowledges and agrees to be bound by the following terms and conditions and represents and warrants that:

    1. Driver Eligibility: The Customer confirms they hold a valid driver's licence and are legally permitted to operate a motor vehicle in British Columbia.

2. Permitted Use and Return: The Vehicle may only be operated by the Customer identified above for the purpose of a test drive. The Vehicle must be returned to the Dealer by the date and time specified above. Failure to return the Vehicle as required may result in the Dealer pursuing all available legal remedies, including civil action. 

3. Compliance with Laws: The Customer agrees to operate the Vehicle in compliance with all applicable laws, statutes, regulations, or by-laws, including the Motor Vehicle Act. 

4. Responsibility for Fines and Charges: The Customer shall be solely responsible for any fines, penalties, fees, tolls, towing charges, storage charges, impound fees, administrative penalties, or other costs arising from or in connection with the Customer’s use or operation of the Vehicle during the test drive.

5. Accidents and Damage: The Customer shall immediately notify the Dealer of any accident, damage, or mechanical issue involving the Vehicle and shall comply with all legal reporting requirements and fully cooperate with the Dealer and any insurer in connection with any investigation or insurance claim.

6. Vehicle Damage and Reimbursement: The Customer agrees to reimburse the Dealer for any loss of or damage to the Vehicle resulting from the Customer’s negligence, unlawful conduct, or breach of this Agreement, including any applicable insurance deductible or uninsured loss incurred by the Dealer.

7. Prohibited Uses: The Customer shall not: 
(a) operate the Vehicle while impaired by alcohol, drugs, or any substance that may affect their ability to safely operate a motor vehicle; 
(b) use the Vehicle for racing, speed testing, towing, ride-sharing, delivery, or any commercial purpose; 
(c) permit any other person to operate the Vehicle without the Dealer’s prior consent; 
(d) use the Vehicle for any unlawful or prohibited purpose; or 
(e) drive the Vehicle outside of the province of British Columbia. 

8. Vehicle Condition: The Customer acknowledges that the Vehicle was received in good condition, subject only to any pre-existing damage noted by the Dealer.

9. Ownership: The Customer acknowledges that ownership of the Vehicle remains with the Dealer at all times and this Agreement does not grant or transfer any ownership or proprietary interest in the Vehicle.

10. Responsibility and Indemnity: The Customer acknowledges that they are fully responsible for the Vehicle while it is in their possession or control and agrees to indemnify and hold harmless the Dealer and its directors, officers, employees, contractors, and agents from and against any claims, losses, damages, liabilities, costs, or expenses arising out of or in connection with the Customer’s use or operation of the Vehicle. 

By signing below, the Customer acknowledges that they have read, understood, and agree to be bound by the terms and conditions of this Agreement and accept full responsibility for the Vehicle while it is in their possession or control.
    `;
  }
  const caslCompliantMessage = `*By continuing, you agree to receive forms and updates from Making Auto Easy on behalf of Applewood Auto Group such as your credit application and personalized updates to assist your purchase. You can opt out anytime.`;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setDisabled(quizQuestions[currentQuestionViewIndex]?.disabled);
  }, [quizQuestions]);

  useEffect(() => {
    const financeApprovalCheckedOff = !financePreApproval; //&& !testDriveChildQuestion?.prefilled;
    setQuizQuestions(
      produce((quizQuestionsDraft) => {
        quizQuestionsDraft[currentQuestionViewIndex].children[
          childIndex
        ].financePreApproval = financePreApproval;
        if (financeApprovalCheckedOff) {
          quizQuestionsDraft[currentQuestionViewIndex].children[
            childIndex
          ].applicantDetails = [
            {
              signature: firstApplicantSignatureData,
            },
          ];
        }
      })
    );
    if (financeApprovalCheckedOff) {
      setTotalApplicants(1);
    }
  }, [financePreApproval]);

  useEffect(() => {
    setQuizQuestions(
      produce((quizQuestionsDraft) => {
        const child =
          quizQuestionsDraft[currentQuestionViewIndex].children[childIndex];
        child.testDriveApproval = testDriveApproval;
        if (testDriveApproval) {
          if (!child.testDriveApprovalDetails)
            child.testDriveApprovalDetails = {
              customerInfo: { name: "", address: "", phone: "" },
              vehicleInfo: {
                year: "",
                make: "",
                model: "",
                stock: "",
                demonstrationLicencePlate: "",
              },
              testDrivePeriod: {
                dateOut: "",
                timeOut: "",
                dateReturnBy: "",
                timeReturnBy: "",
              },
            };
        }
      })
    );
  }, [testDriveApproval]);

  const handleSmsOptIn = async () => {
    const phoneNumber = user?.phoneNo;
    if (!phoneNumber) {
      setSmsOptInStatus("error");
      setFinancingFormStatus("No phone number available for the user.");
      return;
    }
    setSmsOptInStatus("loading");
    setFinancingFormStatus("Sending F&I form...");

    try {
      const response = await sendFinanceAndInsuranceFormText(user?._id);

      if (response) {
        setSmsOptInStatus("success");
        setFinancingFormStatus(
          `F&I form successfully sent to +1 ${phoneNumber}`
        );
      } else {
        setSmsOptInStatus("error");
        setFinancingFormStatus(`Failed to send F&I form to +1 ${phoneNumber}`);
      }
    } catch (error) {
      console.error("API error:", error);
      setSmsOptInStatus("error");
      setFinancingFormStatus(
        `Error occurred while sending to +1 ${phoneNumber}`
      );
    }
  };

  return (
    <>
      <CheckboxWithCollapsibleText
        text="Test Drive Approval"
        collapsibleTextHeader="Test Drive Disclaimer"
        collapsibleText={testDriveApprovalDisclaimer}
        checkBoxState={testDriveApproval}
        toggleCheckBox={setTestDriveApproval}
        disabled={disabled}
      />
      {testDriveApproval && !dealershipId.startsWith("morrey") && (
        <TestDriveFormFields
          quizQuestions={quizQuestions}
          setQuizQuestions={setQuizQuestions}
          currentQuestionViewIndex={currentQuestionViewIndex}
          childIndex={childIndex}
          user={user}
        />
      )}
      {!dealershipId.startsWith("morrey") && (
        <CheckboxWithCollapsibleText
          text="Finance Pre-Approval"
          collapsibleTextHeader="Finance Pre-Approval Disclaimer"
          collapsibleText={financePreApprovalDisclaimer}
          checkBoxState={financePreApproval}
          toggleCheckBox={setFinancePreApproval}
          disabled={disabled}
        />
      )}

      {Array.from({ length: totalApplicants }, (_, index) => index).map(
        (applicantInstance, index) => {
          return (
            <ApplicantDetailsForm
              applicantInstance={applicantInstance}
              setTotalApplicants={setTotalApplicants}
              totalApplicants={totalApplicants}
              key={index}
              quizQuestions={quizQuestions}
              setQuizQuestions={setQuizQuestions}
              currentQuestionViewIndex={currentQuestionViewIndex}
              childIndex={childIndex}
              financePreApproval={financePreApproval}
            />
          );
        }
      )}
      <FormFieldWithLabel
        label="Additional Notes"
        type="textbox"
        id="testdrive-form-notes"
        placeholder=""
        value={additionalNotes}
        r={4}
        c={2}
        onChange={(e) => {
          setQuizQuestions(
            produce((quizQuestionsDraft) => {
              quizQuestionsDraft[currentQuestionViewIndex].children[
                childIndex
              ].additionalNotes = e.target.value;
            })
          );
          setAdditionalNotes(e.target.value);
        }}
        disabled={disabled}
        errorMessage={""}
      />

      {DEALERSHIP_CONFIG[dealershipId]?.textingFinancingFormEnabled && (
        <div style={{ width: "100%" }}>
          <button
            className="sendform_btn"
            onClick={async (e) => {
              e.preventDefault();

              await trackEvent({
                eventName: "F&I_TEXT_SENT",
                role: "SALESPERSON",
                domain: "dealer-sales-copilot",
                payload: {
                  customerId: user?._id || "",
                },
              });

              await handleSmsOptIn();
            }}
            disabled={smsOptInStatus === "loading"}
          >
            <LuSend className="icon" />
            <span>Send F&I Form</span>
          </button>

          {financingFormStatus && (
            <div className={`financing-form-status ${smsOptInStatus}`}>
              {smsOptInStatus === "loading" && (
                <span className="spinner"></span>
              )}
              <span>{financingFormStatus}</span>
            </div>
          )}
          <CollapsibleText
            textHeader={"Credit application disclaimer"}
            text={caslCompliantMessage}
            showText={showCaslDisclaimer}
            setShowText={setShowCaslDisclaimer}
          />
        </div>
      )}
    </>
  );
}
